import React, { Fragment, useEffect, useMemo, useState } from "react";
import { IMAGE_END_POINT } from "../../Api/EndPoints";
import "./CatalogueCard.css";
import adjustPrice from "../../Utils/AdjustPrice";
import moment from "moment";

import CatalogueItemCard from "./CatalogueItemCard";
import { useDispatch, useSelector } from "react-redux";
import TextInput from "../Common/FormElements/TextInput/TextInput";
import { addServPackingToCart } from "../../REDUX/actions/approvisionnement";
import Button from "../Common/FormElements/Button/Button";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { updateCart } from "../../REDUX/actions/catalogue";
import AddToCartButton from "./AddToCartButton";
import { getDiscount } from "../Commandes/ModifyOrder/orderUtils";
import useCanSeePrices, { useCanOrder } from "../../hooks/useCanSeePrices";

const tabsMap = {
  Products: "Produits",
  Accessories: "Accessoires",
  "Marketing Products": "Produits marketing",
  "Spare Part": "Pièce Détachée",
  Services: "Services",
  "Other branded products": "Produits d'autres marques",
  SPA: "SPA",
  "SWIM SPA": "SPA DE NAGE",
  SAUNA: "SAUNA",
};

const CatalogCard = ({
  product,
  activeTab,
  searchInput,
  productsactiveTab,
}) => {
  const { t, i18n } = useTranslation("common");
  const [servicePriceHT, setservicePriceHT] = useState(0);
  const [servicePriceTTC, setservicePriceTTC] = useState(0);
  const { cart } = useSelector((state) => state.catalogueReducer);
  const { user } = useSelector((state) => state.userReducer);

  // A store's commercial sees the store's view: stock / appro and its date, no
  // container name, the public TTC price only — and no cart button.
  const isReseller = [0, 7].includes(user?.attributes?.userRole);
  const canSeePrices = useCanSeePrices();
  const canOrder = useCanOrder();

  const adminGroupedPackings = useMemo(() => {
    const groupedPackings = [...(product?.attributes?.groupedPackings || [])];
    const sortedPackings = [...groupedPackings]?.sort((a, b) => {
      if (a.arrived && b.arrived) {
        const dateA = a?.arrivalDate || a?.estimatedArrivalDate;
        const dateB = b?.arrivalDate || b?.estimatedArrivalDate;
        return `${dateA ?? ""}_${a.cuve ?? ""}_${a.panel ?? ""}` >
          `${dateB ?? ""}_${b.cuve ?? ""}_${b.panel ?? ""}`
          ? 1
          : -1;
      }
      if (a.arrived) return -1;
      if (b.arrived) return 1;
      const dateA = a?.arrivalDate || a?.estimatedArrivalDate;
      const dateB = b?.arrivalDate || b?.estimatedArrivalDate;
      return `${dateA ?? ""}_${a.cuve ?? ""}_${a.panel ?? ""}` >
        `${dateB ?? ""}_${b.cuve ?? ""}_${b.panel ?? ""}`
        ? 1
        : -1;
    });

    return sortedPackings;
  }, [product]);

  const resellerGroupedPackings = useMemo(() => {
    const groupedPackings = (product?.attributes?.groupedPackings || []).map(
      (p) => ({ ...p })
    );

    const sortedPackings = [...groupedPackings].sort((a, b) => {
      if (a.arrived && b.arrived) {
        const dateA = a?.arrivalDate || a?.estimatedArrivalDate;
        const dateB = b?.arrivalDate || b?.estimatedArrivalDate;
        const keyA =
          product?.attributes?.productType === 1
            ? `${dateA}_${a.cuve || ""}_${a.panel || ""}`
            : `${dateA}`;
        const keyB =
          product?.attributes?.productType === 1
            ? `${dateB}_${b.cuve || ""}_${b.panel || ""}`
            : `${dateB}`;

        return keyA > keyB ? 1 : -1;
      }
      if (a.arrived) return -1;
      if (b.arrived) return 1;
      const dateA = a?.arrivalDate || a?.estimatedArrivalDate;
      const dateB = b?.arrivalDate || b?.estimatedArrivalDate;
      const keyA =
        product?.attributes?.productType === 1
          ? `${dateA}_${a.cuve || ""}_${a.panel || ""}`
          : `${dateA}`;
      const keyB =
        product?.attributes?.productType === 1
          ? `${dateB}_${b.cuve || ""}_${b.panel || ""}`
          : `${dateB}`;

      return keyA > keyB ? 1 : -1;
    });

    const result = sortedPackings.reduce((acc, packing) => {
      const arrived = packing.arrived ? "arrived" : "not arrived";
      const cuve = packing.cuve || "";
      const panel = packing.panel || "";
      const key =
        product?.attributes?.productType === 1
          ? `${arrived}_${cuve}_${panel}`
          : `${arrived}`;

      if (!acc[key]) {
        acc[key] = {
          ...packing,
          packings: [...(packing.packings || [])],
        };
      } else {
        const existing = acc[key].packings || [];
        const current = packing.packings || [];
        acc[key].packings = Array.from(new Set([...existing, ...current]));
      }

      return acc;
    }, {});

    return result;
  }, [product]);

  const discount = getDiscount({
    discount: product?.attributes?.discountHT,
    discountStartDate: product?.attributes.discountStartDate,
    discountEndDate: product?.attributes?.discountEndDate,
  });

  const discountTTC = getDiscount({
    discount: product?.attributes?.discountTTC,
    discountStartDate: product?.attributes.discountStartDate,
    discountEndDate: product?.attributes?.discountEndDate,
  });

  const priceAfterDiscount = product?.attributes?.retailerPriceHT - discount;
  const priceAfterDiscountTTC =
    product?.attributes?.publicPriceTTC - discountTTC;

  const dispatch = useDispatch();

  const { language } = i18n;
  const navigate = useNavigate();

  const handleNavigate = () => {
    navigate(`/catalogue/produit/${product.id}`);
  };

  const addServiceToCart = () => {
    dispatch(
      addServPackingToCart(
        product?.id,
        {
          customRetailerPriceHT: servicePriceHT,
          customPublicPriceTTC: servicePriceTTC,
        },
        {
          activeTab: tabsMap[activeTab],
          searchInput,
          productsactiveTab: tabsMap[productsactiveTab],
        },
        cart?.exchangeRate
      )
    );
  };

  useEffect(() => {
    const initialValueTTC = product?.attributes?.publicPriceTTC
      ? product?.attributes?.publicPriceTTC?.toFixed(2).toString()
      : (product?.attributes?.retailerPriceHT * 1.2).toFixed(2).toString();

    const initialValueHT = product?.attributes?.retailerPriceHT
      ? product?.attributes?.retailerPriceHT?.toFixed(2).toString()
      : 0;

    setservicePriceHT(initialValueHT);
    setservicePriceTTC(initialValueTTC);
  }, [product.attributes.retailerPriceHT, product.attributes.publicPriceTTC]);

  const image = product?.attributes?.attachments?.find((item) => {
    return item?.file?.mime.includes("image");
  })?.file?.url;

  return (
    <div
      className="newCatalogueCardContainer p-4 border"
      onClick={handleNavigate}
    >
      <div className="d-flex flex-column alignC w100 gap-2 flex-1 p-2">
        <img
          className="newCatalogueImg"
          src={`${IMAGE_END_POINT}${image}`}
          alt=""
        />

        {product?.attributes?.productType !== 5 &&
          product?.attributes?.productType !== 6 &&
          (product.attributes.totalNumberOfPackings === 0 ? (
            <div
              className={`text-red f14 bold w100 ${
                product.attributes.productType === 5 ||
                product.attributes.productType === 6
                  ? "mb-5"
                  : "mt-5"
              }`}
            >
              {product.attributes.productType !== 5 ||
              product.attributes.productType === 6
                ? t("Out of stock")
                : ""}
            </div>
          ) : (
            <div className="f14 bold w100 d-flex justify-content-between">
              <div className="text-green">
                {product.attributes.totalNumberArrived} {t("In stock")}
              </div>
              <div className="text-brown">
                {product.attributes.totalNumberUnarrived} {t("in supply")}
              </div>
            </div>
          ))}
        <div className="f20 bold quickSand ultraBlack w100">
          {language === "en"
            ? product.attributes.name_en ?? product.attributes.name
            : product.attributes.name}
        </div>
        <div className="f20 bold quickSand ultraBlack w100 mt-2"></div>
        <div className=" w100">
          {!canSeePrices ? null : product.attributes.productType !== 5 &&
            product.attributes.productType !== 6 ? (
            <div className="d-flex flex-column">
              <p
                className={`bold text-softBlack mb-0 ${
                  discount > 0 ? "text-decoration-line-through f14" : "f16"
                }`}
              >
                {t("Price")} :{" "}
                {product.attributes.retailerPriceHT
                  ? adjustPrice(
                      //   cart?.majorAccountOrder &&
                      // cart?.currency === "Dollar" &&
                      // cart?.exchangeRate
                      //   ? product.attributes.retailerPriceHT / cart?.exchangeRate :
                      product?.attributes?.retailerPriceHT
                        ?.toFixed(2)
                        .toString()
                    )
                  : "0.00"}
                {/* {cart?.majorAccountOrder && cart?.currency === "Dollar"
                ? "$" :*/}
                € {t("Excl. Tax")}
              </p>
            </div>
          ) : (
            <div className="d-flex flex-column gap-3">
              <div
                className="f16 bold text-softBlack mb-0 d-flex align-items-center "
                onClick={(e) => e.stopPropagation()}
              >
                <span className="col-3">{t("Price excl. tax")} : </span>
                <TextInput
                  name="value"
                  className={"col-9"}
                  // deviseType={isParticulier ? "TTC" : "HT"}
                  value={
                    // cart?.majorAccountOrder &&
                    // cart?.currency === "Dollar" &&
                    // cart?.exchangeRate
                    //   ? servicePriceHT / cart?.exchangeRate :

                    servicePriceHT - discount
                  }
                  inputClassName={"f14"}
                  placeholderStyle={"f14"}
                  callback={(e) =>
                    setservicePriceHT(
                      // cart?.majorAccountOrder &&
                      //   cart?.currency === "Dollar" &&
                      //   cart?.exchangeRate
                      //   ? e.target.value * cart?.exchangeRate
                      e.target.value
                    )
                  }
                  type="number"
                  surfixText={
                    // cart?.majorAccountOrder && cart?.currency === "Dollar"
                    product.attributes.productType === 6 ? "$" : "€"
                  }
                />
              </div>
              <div
                className="f16 bold text-softBlack mb-0 d-flex align-items-center"
                onClick={(e) => e.stopPropagation()}
              >
                <span className="col-3">{t("Price incl. tax")} : </span>
                <TextInput
                  name="value"
                  className={"col-9"}
                  // deviseType={"TTC"}
                  value={
                    // cart?.majorAccountOrder &&
                    // cart?.currency === "Dollar" &&
                    // cart?.exchangeRate
                    //   ? servicePriceTTC / cart?.exchangeRate
                    servicePriceTTC - discountTTC
                  }
                  inputClassName={"f14"}
                  placeholderStyle={"f14"}
                  callback={(e) =>
                    setservicePriceTTC(
                      // cart?.majorAccountOrder &&
                      //   cart?.currency === "Dollar" &&
                      //   cart?.exchangeRate
                      //   ? e.target.value * cart?.exchangeRate
                      e.target.value
                    )
                  }
                  type="number"
                  surfixText={
                    // cart?.majorAccountOrder && cart?.currency === "Dollar"
                    //   ? "$"
                    //   : "€"
                    product.attributes.productType === 6 ? "$" : "€"
                  }
                />
              </div>
            </div>
          )}
          {product.attributes.productType !== 5 &&
            product.attributes.productType !== 6 && (
              <div className="d-flex flex-column">
                <p
                  className={`f14 bold text-softBlack mb-0 ${
                    discountTTC > 0 ? "f14 text-decoration-line-through" : "f16"
                  }`}
                >
                  {t("Recommended Retail Price")} :{" "}
                  {product?.attributes?.publicPriceTTC
                    ? adjustPrice(
                        product?.attributes?.publicPriceTTC
                          // )
                          .toFixed(2)
                          .toString()
                      )
                    : "0.00"}
                  €
                </p>
                {discount > 0 && (
                  <>
                    <p className={`f16 bold text-green-800 mt-1 mb-0`}>
                      {adjustPrice(priceAfterDiscount?.toFixed(2).toString())}€{" "}
                      {t("Excl. Tax")}
                    </p>
                  </>
                )}
                {discountTTC > 0 && (
                  <>
                    <p className={`f16 bold text-green-800 mb-0`}>
                      {adjustPrice(
                        priceAfterDiscountTTC?.toFixed(2).toString()
                      )}
                      € {t("Incl. Tax")}
                    </p>
                    <p className={`f16 bold text-brightGrey my-0`}>
                      {t("Discount until")} :{" "}
                      {moment(product?.attributes?.discountEndDate).format(
                        "DD/MM/yyyy"
                      )}
                    </p>
                  </>
                )}
              </div>
            )}
        </div>
        {canOrder &&
          (product?.attributes?.productType === 5 ||
          product?.attributes?.productType === 6) && (
          <div className="d-flex w100 gap-3 mt-5 justify-content-center">
            <Button
              className="br30 bold flex-1 my-3"
              onClick={(e) => {
                e.stopPropagation();
                product?.attributes?.productType === 6 &&
                  dispatch(
                    updateCart(cart?.id, {
                      majorAccountOrder: true,
                      currency: "Dollar",
                    })
                  );
                addServiceToCart();
              }}
            >
              {t("Add to cart")}
            </Button>
          </div>
        )}
      </div>
      {product?.attributes?.productType !== 5 &&
        product?.attributes?.productType !== 6 && (
          <div
            className={`packingsList d-flex flex-column w100 gap-2 flex-1 p-2 mt-3 ${
              isReseller ? "reseller" : ""
            }`}
          >
            {isReseller && product?.attributes?.productType === 1
              ? Object.values(resellerGroupedPackings)?.map(
                  (packingGroup, index) => {
                    return (
                      <Fragment key={index}>
                        {index > 0 && <hr className="my-0 opacity-25" />}
                        <div className="my-1 d-flex justify-content-between align-items-center w-100">
                          <div
                            className={`flex-1 f14 bold ${
                              packingGroup.arrived ? "text-green" : "text-brown"
                            }`}
                          >
                            {packingGroup?.packings?.length || 0}x{" "}
                            {packingGroup.cuve ? `${packingGroup.cuve}` : ""}
                            {packingGroup.panel
                              ? `, ${packingGroup.panel}`
                              : ""}
                            {!packingGroup.arrived && <>, {t("Appro")}</>}
                            {packingGroup.arrived
                              ? ""
                              : packingGroup.arrivalDate
                              ? `,${t("Arrives on")} ${moment(
                                  packingGroup.arrivalDate
                                ).format("DD/MM/YYYY")}`
                              : packingGroup?.estimatedArrivalDate
                              ? `,${t("Estimated on")} ${moment(
                                  packingGroup.estimatedArrivalDate
                                ).format("DD/MM/YYYY")}`
                              : ""}
                          </div>
                          <AddToCartButton
                            packingItemId={packingGroup.packings[0].id}
                            activeTab={activeTab}
                            searchInput={searchInput}
                            productsactiveTab={productsactiveTab}
                          />
                        </div>
                      </Fragment>
                    );
                  }
                )
              : (isReseller
                  ? Object.values(resellerGroupedPackings)
                  : adminGroupedPackings
                ).map((packingGroup, index) => {
                  return (
                    <div
                      className="my-3"
                      key={`${index}_${packingGroup.containerName}`}
                    >
                      <div
                        className={`flex-1 f14 bold ${
                          packingGroup.arrived ? "text-green" : "text-brown"
                        }`}
                      >
                        {packingGroup.packings?.length}x{" "}
                        {packingGroup.cuve ? `${packingGroup.cuve}, ` : ""}
                        {packingGroup.panel}
                        {!isReseller && !packingGroup.arrived && (
                          <>, {t("Appro")}</>
                        )}{" "}
                        {!isReseller && packingGroup.containerName}{" "}
                        {packingGroup.arrived
                          ? ""
                          : packingGroup.arrivalDate
                          ? `,${t("Arrives on")} ${moment(
                              packingGroup.arrivalDate
                            ).format("DD/MM/YYYY")}`
                          : packingGroup?.estimatedArrivalDate
                          ? `,${t("Estimated on")} ${moment(
                              packingGroup.estimatedArrivalDate
                            ).format("DD/MM/YYYY")}`
                          : ""}
                      </div>
                      <CatalogueItemCard
                        product={product}
                        packingGroup={packingGroup}
                        activeTab={activeTab}
                        searchInput={searchInput}
                        productsactiveTab={productsactiveTab}
                      />
                    </div>
                  );
                })}
          </div>
        )}
    </div>
  );
};

export default CatalogCard;
